<template>
    <v-autocomplete
        v-model="model"
        :items="items"
        :loading="loading"
        :label="label"
        variant="outlined"
        density="compact"
        :menu-icon="menuIcon"
        :noDataText="noDataI18nKey || `common.form.noDataText.${items.length ? 'filterNoMatch' : 'select'}`"
        :custom-filter="customFilter || customKeysFilter"
        :no-filter="noFilter"
        :hideDetails="hideDetails && fixedServerError"
        :error-messages="!fixedServerError ? serverError : ''"
    >
        <template
            v-if="$slots.selection"
            #selection="{ item }"
        >
            <label :class="`v-autocomplete__selection--${propsValues.selectedStyle}`">
                <slot
                    name="selection"
                    :item="item"
                    :itemRaw="item?.raw"
                ></slot>
            </label>
        </template>

        <template
            v-if="$slots.item"
            #item="{ props, item }"
        >
            <span :class="disabledItem(props)">
                <slot
                    name="item"
                    :props="props"
                    :item="item"
                    :itemRaw="item?.raw"
                    data-testid="autocomplete-item"
                ></slot>
            </span>
        </template>
        <template #append-item>
            <div v-intersect="endIntersect" />
        </template>
    </v-autocomplete>
</template>

<script setup>
import { ref, computed, watch } from 'vue';

const propsValues = defineProps({
    modelValue: {
        type: [String, Number, Object]
    },
    items: {
        type: Array,
        default: () => []
    },
    loading: {
        type: Boolean,
        default: false
    },
    label: {
        type: String,
        default: ''
    },
    menuIcon: {
        type: String,
        default: 'chevronDown'
    },
    noDataI18nKey: {
        type: String
    },
    customFilter: {
        type: Function
    },
    filterKeys: {
        type: Array
    },
    selectedStyle: {
        type: String,
        default: 'dotted'
    },
    noFilter: {
        type: Boolean,
        default: false
    },
    allowDisabledOptions: {
        type: Boolean,
        default: false
    },
    hideDetails: {
        type: Boolean,
        default: false
    },
    serverError: {
        type: String,
        default: ''
    }
});

const emit = defineEmits(['update:modelValue', 'infinitescroll:items']);

const fixedServerError = ref(true);

const model = computed({
    get() {
        return propsValues.modelValue;
    },
    set(newValue) {
        emit('update:modelValue', newValue);
        document.activeElement.blur();

        if (propsValues.serverError) {
            fixedServerError.value = true;
        }
    }
});

const disabledItem = item => {
    if (!propsValues.allowDisabledOptions) {
        return '';
    }

    return !item?.title?.ordersAllowed ? 'v-autocomplete__disabled' : '';
};

const customKeysFilter = propsValues.filterKeys
    ? (itemID, queryText, item) => {
          const itemObj = item.raw;
          const searchText = queryText.toLowerCase();

          return propsValues.filterKeys.some(key => itemObj?.[key]?.toLowerCase().indexOf(searchText) > -1);
      }
    : undefined;

const endIntersect = (entries, observer, isIntersecting) => {
    if (isIntersecting) {
        emit('infinitescroll:items');
    }
};

watch(
    () => propsValues.serverError,
    newValue => {
        fixedServerError.value = !newValue;
    },
    { immediate: true }
);

watch(
    () => propsValues.modelValue,
    () => {
        fixedServerError.value = true;
    },
    { immediate: true }
);
</script>

<style lang="scss" scoped>
:deep(.v-autocomplete__selection) {
    max-width: 90%;
}
.v-autocomplete__selection {
    &--dotted {
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
    }
}
.v-autocomplete__disabled {
    pointer-events: none;
    opacity: 0.4;
}
</style>
